import Vue from 'vue';
import {isFunction} from 'lodash-es';
import Router from './async';
import {SearchService} from '@/services'
import {Breadcrumb, Search} from '@/services/models';
import {getMeta, getRoute, hasPermissionToRoute, forceLoginForModule, setSEOForRoute} from './helpers';

Vue.use(Router);
const router = new Router({
   mode: 'history',
   base: '/',

   /**
    * To enable SPA (vue-router) routes with MVC projects, we need to configure the App_Start/RouteConfig.cs to allow
    * specific routes to not conflict with existing ones. Make sure to configure it both places to enable routes.
    * - See the "Vue-router" region in App_Start/RouteConfig.cs
    *   or wiki: http://intranett.odindata.no/odinpedia/index.php/Setup_Vue_with_existing_MVC_project#Setup_MVC_routes_together_with_SPA_routes
    */
   routes: [
      {
         path: '/',
         name: 'frontpage',
         component: () => import(/* webpackChunkName: "frontpage" */ '@/views/frontpage/FrontPage.vue'),
         meta: {
            actions: [
               app => app.$store.dispatch('search/getMarkersForSites')
            ],
            breadcrumb: new Breadcrumb({
               text: 'Hovedside',
               to: '/'
            })
         }
      },
      {
         path: '/minside/:tab',
         name: 'mypage',
         component: () => import(/* webpackChunkName: "mypage" */ '@/views/mypage/MyPage.vue'),
         meta: {
            authenticated: true,
            breadcrumb: new Breadcrumb({
               text: 'Min side',
               to: '/minside',
               exact: true
            })
         },
         children: [
            {
               path: '/minside/profil',
               name: 'mypage/profile',
               component: () => import(/* webpackChunkName: "mypage" */ '@/views/mypage/views/profile/Profile.vue'),
               props: async (route) => {
                  if (route.query.chainId) route.meta.layout = 'chain-layout'
               },
               alias: ['/minside'],
               meta: {
                  authenticated: true,
                  breadcrumb: new Breadcrumb({
                     text: 'Min profil',
                     to: '/minside/profil',
                     disabled: true
                  })
               }
             },
            {
                 path: '/minside/minesalonger',
                 name: 'mypage/my-salons',
                 component: () => import(/* webpackChunkName: "mypage" */ '@/views/mypage/views/my-salons/MySalons.vue'),
                 props: async (route) => {
                  if (route.query.chainId) route.meta.layout = 'chain-layout'
               },
                 meta: {
                     authenticated: true,
                     breadcrumb: new Breadcrumb({
                         text: 'Mine salonger',
                         to: '/minside/minesalonger',
                     })
                 }
             },
            {
               path: '/minside/besok/:id?',
               name: 'mypage/visits',
               component: () => import(/* webpackChunkName: "mypage" */ '@/views/mypage/views/visits/Visits.vue'),
               props: async (route) => {
                  if (route.query.chainId) route.meta.layout = 'chain-layout'
               },
               meta: {
                  authenticated: true,
                  breadcrumb: new Breadcrumb({
                     text: 'Mine besøk',
                     to: '/minside/besok'
                  })
               }
            },
            {
               path: '/minside/favoritter',
               name: 'mypage/favourites',
               component: () => import(/* webpackChunkName: "mypage" */ '@/views/mypage/views/favourites/Favourites.vue'),
               props: async (route) => {
                  if (route.query.chainId) route.meta.layout = 'chain-layout'
               },
               meta: {
                  authenticated: true,
                  breadcrumb: new Breadcrumb({
                     text: 'Mine favoritter',
                     to: '/minside/favoritter'
                  })
               }
            },
            {
               path: '/minside/fixit-gavekort',
               name: 'mypage/fixit-giftcard',
               component: () => import(/* webpackChunkName: "mypage" */ '@/views/mypage/views/fixit-giftcard/FixitGiftcard.vue'),
               meta: {
                  authenticated: true,
                  breadcrumb: new Breadcrumb({
                     text: 'Fixit.no-gavekort',
                     to: '/minside/fixit-gavekort'
                  })
               }
            },
            {
               path: '/minside/ordrehistorikk',
               name: 'mypage/purchase-history',
               component: () => import(/* webpackChunkName: "mypage" */ '@/views/mypage/views/purchase-history/PurchaseHistory.vue'),
               meta: {
                  authenticated: true,
                  breadcrumb: new Breadcrumb({
                     text: 'Ordrehistorikk',
                     to: '/minside/ordrehistorikk'
                  })
               }
            },
            {
               path: '/minside/fordelsprogram/:id?',
               name: 'mypage/reward',
               component: () => import(/* webpackChunkName: "mypage" */ '@/views/mypage/views/reward/Reward.vue'),
               props: async (route) => {
                  if (route.query.chainId) route.meta.layout = 'chain-layout'
               },
               meta: {
                  authenticated: true,
                  breadcrumb: new Breadcrumb({
                     text: 'Fordelsprogram',
                     to: '/minside/fordelsprogram'
                  })
               }
            },
            {
               path: '/minside/samtykker',
               name: 'mypage/consents',
               component: () => import(/* webpackChunkName: "mypage" */ '@/views/mypage/views/consents/Consents.vue'),
               props: async (route) => {
                  if (route.query.chainId) route.meta.layout = 'chain-layout'
               },
               meta: {
                  authenticated: true,
                  breadcrumb: new Breadcrumb({
                     text: 'Mine samtykker',
                     to: '/minside/samtykker'
                  })
               }
            }
         ]
      },
      {
         path: '/kjop-gavekort',
         name: 'buy-giftcard',
         component: () => import(/* webpackChunkName: "giftcard" */ '@/views/giftcard/BuyGiftcard.vue'),
         meta: {
            breadcrumb: new Breadcrumb({
               text: 'Kjøp gavekort',
               to: '/kjop-gavekort',
               exact: true
            })
         }
      },
      {
         path: '/aktiver-gavekort',
         name: 'activate-giftcard',
         component: () => import(/* webpackChunkName: "giftcard" */ '@/views/giftcard/ActivateGiftcard.vue'),
         meta: {
            breadcrumb: new Breadcrumb({
               text: 'Aktiver gavekort',
               to: '/aktiver-gavekort',
               exact: true
            })
         }
      },
      /*Erik 10.12.2019: This route can be removed. Used for testing  the PFD/print pop-up */
      {
         path: '/print-gavekort',
         name: 'print-giftcard',
         component: () => import(/* webpackChunkName: "giftcard" */ '@/views/giftcard/components/print-giftcard/Page.vue'),
         meta: {
            breadcrumb: new Breadcrumb({
               text: 'Print gavekort',
               to: '/print-gavekort',
               exact: true
            })
         }
      },
      {
         path: '/steder',
         name: 'areas',
         component: () => import(/* webpackChunkName: "area" */ '@/views/areas/Page.vue'),
         meta: {
            breadcrumb: new Breadcrumb({
               text: 'Steder',
               to: '/steder',
               exact: true
            })
         },
         children: []
      },
      {
         path: '/steder/:url',
         name: 'areas/area',
         component: () => import(/* webpackChunkName: "area" */ '@/views/areas/Area.vue'),
         meta: {
            actions: [
               app => app.$store.dispatch('search/getMarkersForSites')
            ],
            breadcrumb: new Breadcrumb({
               text: 'By',
               to: `/steder/${name}`
            })
         }
      },
      {
         path: '/steder/:area/:district',
         name: 'areas/area/district',
         component: () => import(/* webpackChunkName: "area" */ '@/views/areas/Area.vue'),
         meta: {
            breadcrumb: new Breadcrumb({
               text: `Distrikt`,
               to: `/steder/${name}`
            })
         }
      },
      {
         path: '/behandlinger',
         name: 'treatments',
         component: () => import(/* webpackChunkName: "giftcard" */ '@/views/treatments/Page.vue'),
         meta: {
            breadcrumb: new Breadcrumb({
               text: 'Behandlinger',
               to: '/behandlinger',
               exact: true
            })
         }
      },
      {
         path: '/behandlinger/:url',
         name: 'treatment',
         props: true,
         component: () => import(/* webpackChunkName: "giftcard" */ '@/views/treatments/Treatment.vue'),
         meta: {
            actions: [
               app => app.$store.dispatch('search/getMarkersForSites')
            ],
            breadcrumb: new Breadcrumb({
               text: 'Behandlinger',
               to: '/behandlinger'
            })
         }
      },
      {
         path: '/sok',
         name: 'search',
         props: ({query}) => new Search(query),
         component: () => import(/* webpackChunkName: "search" */ '@/views/search/SearchResult.vue'),
         meta: {
            header: {
               dense: true,
               fullWidth: true
            }
         }
      },
      {
         path: '/nettbutikk/:chainId/',
         name: 'webshopChain',
         props: true,
         component: () => import(/* webpackChunkName: "chainwebshop" */ '@/views/webshop/StoreFront.vue'),
         meta: {
            breadcrumb: () => {
               return new Breadcrumb({
                  text: 'Webshop',
                  exact: true,
                  to: '/nettbutikk/' + router.history.current.params.chainId
               })
            },
            layout: 'chain-layout'
         },
         children: [
            {
               path: 'produkter',
               name: 'webshopChainProductsCategories',
               props: true,
               component: () => import(/* webpackChunkName: "chainwebshop" */ '@/views/webshop/StoreFront.vue'),
               meta: {
                  breadcrumb: () => {
                     return new Breadcrumb({
                        text: 'Webshop',
                        exact: true,
                        to: '/nettbutikk/' + router.history.current.params.chainId
                     })
                  },
                  layout: 'chain-layout'
               }
            },
            {
               path: 'produkter/:categoryFirst/:categorySecond?/:categoryThird?',
               name: 'webshopChainProductsMainCategories',
               props: true,
               component: () => import(/* webpackChunkName: "chainwebshop" */ '@/views/webshop/StoreFront.vue'),
               meta: {
                  breadcrumb: () => {
                     return new Breadcrumb({
                        text: 'Webshop',
                        exact: true,
                        to: '/nettbutikk/' + router.history.current.params.chainId
                     })
                  },
                  layout: 'chain-layout'
               }
            }
         ]
      },
      {
         path: '/nettbutikk/:chainId/produkt/:productTitle/:productId',
         name: 'webshopChainProductDetails',
         props: true,
         component: () => import(/* webpackChunkName: "chainwebshop" */ '@/views/webshop/views/ProductDetails.vue'),
         meta: {
            // actions: [
            // 	app => app.$store.dispatch('app/setLayout', {layout: 'salon-layout'})
            // ],
            breadcrumb: new Breadcrumb({
               text: 'Product Details',
               to: '',
               disabled: true
            }),
            layout: 'chain-layout'
         }
      },
      {
         path: '/nettbutikk/:chainId/kampanjer/:campaignId/pakketilbud',
         name: 'webshopCampaignBundle',
         props: true,
         component: () => import(/* webpackChunkName: "chainwebshop" */ '@/views/webshop/views/BundleCampaignDetails.vue'),
         meta: {
            // actions: [
            // 	app => app.$store.dispatch('app/setLayout', {layout: 'salon-layout'})
            // ],
            breadcrumb: new Breadcrumb({
               text: 'Bundle Campaign',
               to: '',
               disabled: true
            }),
            layout: 'chain-layout'
         }
      },
      {
         path: '/nettbutikk/:chainId/kampanjer/:campaignId/produkter',
         name: 'webshopCampaignProducts',
         props: true,
         component: () => import(/* webpackChunkName: "chainwebshop" */ '@/views/webshop/StoreFront.vue'),
         meta: {
            breadcrumb: () => {
               return new Breadcrumb({
                  text: 'Webshop',
                  exact: true,
                  to: '/nettbutikk/' + router.history.current.params.chainId
               })
            },
            layout: 'chain-layout'
         }
      },
      {
         path: '/nettbutikk/:chainId/produkter',
         name: 'webshopProducts',
         props: true,
         component: () => import(/* webpackChunkName: "chainwebshop" */ '@/views/webshop/StoreFront.vue'),
         meta: {
            breadcrumb: () => {
               return new Breadcrumb({
                  text: 'Webshop',
                  exact: true,
                  to: '/nettbutikk/' + router.history.current.params.chainId
               })
            },
            layout: 'chain-layout'
         }
      },
      {
         path: '/nettbutikk/:chainId/handlekurv',
         name: 'webshopChainShoppingCartClickAndCollect',
         props: true,
         component: () => import(/* webpackChunkName: "chainwebshop" */ '@/views/webshop/views/ShoppingCartClickAndCollect.vue'),
         meta: {
            // actions: [
            // 	app => app.$store.dispatch('app/setLayout', {layout: 'salon-layout'})
            // ],
            layout: 'chain-layout'
         }
      },
      // {
      //    path: '/nettbutikk/:chainId/checkout-klikk-og-hent/:siteId',
      //    name: 'webshopChainCheckoutClickAndCollect',
      //    props: true,
      //    component: () => import(/* webpackChunkName: "chainwebshop" */ '@/views/webshop/views/CheckoutClickAndCollect.vue'),
      //    meta: {
      //       // actions: [
      //       // 	app => app.$store.dispatch('app/setLayout', {layout: 'salon-layout'})
      //       // ],
      //       layout: 'chain-layout'
      //    }
      // },
      // {
      //    path: '/nettbutikk/:chainId/checkout-klikk-og-hent/:siteId/ordre',
      //    name: 'webshopChainCheckoutClickAndCollectOrderPreview',
      //    props: true,
      //    component: () => import(/* webpackChunkName: "chainwebshop" */ '@/views/webshop/views/CheckoutClickAndCollect.vue'),
      //    meta: {
      //       // actions: [
      //       // 	app => app.$store.dispatch('app/setLayout', {layout: 'salon-layout'})
      //       // ],
      //       layout: 'chain-layout'
      //    }
      // },
      // {
      //    path: '/nettbutikk/:chainId/checkout-completed-klikk-og-hent/:siteId',
      //    name: 'webshopChainCheckoutCompletedClickAndCollect',
      //    props: true,
      //    component: () => import(/* webpackChunkName: "chainwebshop" */ '@/views/webshop/views/CheckoutClickAndCollect.vue'),
      //    meta: {
      //       // actions: [
      //       // 	app => app.$store.dispatch('app/setLayout', {layout: 'salon-layout'})
      //       // ],
      //       layout: 'chain-layout'
      //    }
      // },
      {
         path: '/:treatmentName/:areaName',
         name: 'treatmentAndArea',
         props: async ({params: {treatmentName, areaName}}) => {
            // Fetch treatment and area from server
            let {treatment, area} = await SearchService.GetTreatmentAndArea(treatmentName, areaName);
            if (treatment && area) {
               return new Search({...treatment, ...area});
            }

            // Treatment or area not found, redirect to frontpage
            router.push('/');
         },
         component: () => import(/* webpackChunkName: "search" */ '@/views/search/SearchResult.vue'),
         meta: {
            header: {
               dense: true,
               fullWidth: true
            }
         }
      }
   ]
});
router.beforeEach(async (to, from, next) => {
   // close webshop dialogs guard
   if (from.path.includes('/nettbutikk') && (router.app.$store.getters['webshop/get']('salonPickOptions').dialog || router.app.$store.getters['webshop/get']('addedToCartDialog'))) {
      router.app.$store.dispatch('webshop/set', {key: 'salonPickOptions', value: {dialog: false}})
      router.app.$store.dispatch('webshop/set', {key: 'addedToCartDialog', value: false});
      return next(false)
   }

   let rejected = false;
   let nextRoute = await new Promise((resolve, reject) => {
      // Check if route is not disabled
      if (getMeta(to, 'disabled')) {
         reject();
      }

      // Check if user has permission to this route
      const access = hasPermissionToRoute(to);
      if (!access) {
         if (forceLoginForModule(to.name)) {
            resolve({path: '/', query: {redirectUrl: to.fullPath}});
         } else {
            reject();
         }
      } else {
         resolve();
      }
   }).catch(() => {
      rejected = true;

      // Redirect to frontpage
      setSEOForRoute(getRoute('/'));
      next('/');
   });

   // Scroll to top after navigations
   window.pageYOffset = document.documentElement.scrollTop = document.body.scrollTop = 0;

   // Resolve next
   if (!rejected) {

      // We should only set meta data if it NOT the first initial load.
      // Then we set the meta server side to enable good SEO
      const isFirstRouteLoad = !from.name;
      // Checking for chainId in query or params to skip seo routing and page title changing
      if (!isFirstRouteLoad && !(to.query.chainId || to.params.chainId)) {
         setSEOForRoute(getRoute(nextRoute) || to);
      }

      next(nextRoute);
   }
});

router.afterEach(to => {

   // Set layout (fallback on portal)
   const layout = getMeta(to, 'layout');
   if (layout) {
      router.app.$store.dispatch('app/setLayout', {layout: layout})
   } else {
      router.app.$store.dispatch('app/setLayout', {layout: 'portal-layout'})
   }

   // Exec actions on a route
   const actions = getMeta(to, 'actions');
   if (Array.isArray(actions)) {
      router.app.$nextTick(() => {
         // Schedule actions to be called during the browser's idle periods.
         requestIdleCallback(() => {
            for (const action of actions) {
               isFunction(action) && action(router.app);
            }
         }, {timeout: 2000});
      });
   }
});

export default router;
